import React, { useEffect, useState, useCallback } from "react";
import {
  Users, CalendarDays, List, Settings, Plus, Pencil, Trash2,
  Loader2, X, Save, ChevronLeft, ChevronRight, Check,
} from "lucide-react";
import api, { formatApiError } from "../../lib/api";

const DAYS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const HOURS = Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, "0")}:00`);

export default function ReservasTab() {
  const [section, setSection] = useState("entrenadores");

  const SECTIONS = [
    { id: "entrenadores", icon: Users, label: "Entrenadores" },
    { id: "disponibilidad", icon: CalendarDays, label: "Disponibilidad" },
    { id: "reservas", icon: List, label: "Reservas" },
    { id: "config", icon: Settings, label: "Configuración" },
  ];

  return (
    <div className="p-6 lg:p-8" data-testid="reservas-tab">
      <div className="flex items-center justify-between mb-7">
        <div>
          <h1 className="text-xl font-black tracking-wider">Reservas</h1>
          <p className="text-xs text-white/40 mt-0.5">Gestión de entrenadores, disponibilidad y citas</p>
        </div>
        <div className="flex border border-white/10">
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setSection(s.id)} data-testid={`section-${s.id}`}
              className={`flex items-center gap-1.5 px-3 py-2 text-[10px] font-black tracking-widest uppercase transition-colors ${section === s.id ? "bg-[#FFCC00] text-black" : "text-white/40 hover:text-white"}`}>
              <s.icon size={12} /><span className="hidden sm:inline">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {section === "entrenadores" && <EntrenadoresSection />}
      {section === "disponibilidad" && <DisponibilidadSection />}
      {section === "reservas" && <ReservasSection />}
      {section === "config" && <ConfigSection />}
    </div>
  );
}

// ── ENTRENADORES ──────────────────────────────────────────────────────────────
function EntrenadoresSection() {
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ nombre: "", especialidad: "", bio: "", foto_url: "", activo: true });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true);
    try { setTrainers(await api.get("/admin/trainers").then(r => r.data)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setForm({ nombre: "", especialidad: "", bio: "", foto_url: "", activo: true }); setErr(""); setModal("create"); };
  const openEdit = t => { setForm({ ...t, foto_url: t.foto_url || "" }); setErr(""); setModal(t.id); };

  const handleSave = async () => {
    if (!form.nombre.trim()) { setErr("El nombre es obligatorio"); return; }
    setSaving(true); setErr("");
    try {
      const payload = { nombre: form.nombre.trim(), especialidad: form.especialidad || "", bio: form.bio || "", foto_url: form.foto_url || null, activo: form.activo };
      if (modal === "create") {
        await api.post("/admin/trainers", payload);
      } else {
        await api.put(`/admin/trainers/${modal}`, payload);
      }
      setModal(null); await load();
    } catch (e) { setErr(formatApiError(e?.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async id => {
    if (!window.confirm("¿Eliminar este entrenador?")) return;
    try { await api.delete(`/admin/trainers/${id}`); await load(); }
    catch (e) { alert(formatApiError(e?.response?.data?.detail) || "Error"); }
  };

  return (
    <div data-testid="entrenadores-section">
      <div className="flex justify-end mb-4">
        <button onClick={openCreate} data-testid="add-trainer-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
          <Plus size={12} /> Nuevo entrenador
        </button>
      </div>

      {loading ? <Spinner /> : trainers.length === 0 ? (
        <Empty icon={Users} text="No hay entrenadores todavía." />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {trainers.map(t => (
            <div key={t.id} className={`border ${t.activo ? "border-white/8" : "border-red-500/15 opacity-60"} bg-[#0a0a0a] p-4`} data-testid={`trainer-card-${t.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 bg-black border border-white/10 flex items-center justify-center shrink-0 overflow-hidden">
                  {t.foto_url ? <img src={t.foto_url} alt={t.nombre} className="w-full h-full object-cover" /> : <Users size={20} className="text-white/20" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-black text-sm text-white">{t.nombre}</p>
                  {t.especialidad && <p className="text-[11px] text-[#FFCC00]/70 font-bold">{t.especialidad}</p>}
                  {t.bio && <p className="text-[11px] text-white/35 mt-1 line-clamp-2">{t.bio}</p>}
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-white/5">
                <button onClick={() => openEdit(t)} className="flex-1 flex items-center justify-center gap-1 py-1.5 border border-white/10 text-white/40 hover:border-[#FFCC00]/50 hover:text-[#FFCC00] text-[10px] font-bold tracking-wider uppercase transition-colors">
                  <Pencil size={10} /> Editar
                </button>
                <button onClick={() => handleDelete(t.id)} className="p-1.5 border border-white/10 text-white/25 hover:border-red-500/40 hover:text-red-400 transition-colors">
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <Modal title={modal === "create" ? "Nuevo entrenador" : "Editar entrenador"} onClose={() => setModal(null)}>
          <Fld label="Nombre *"><input value={form.nombre} onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))} className="inp" placeholder="Carlos García" /></Fld>
          <Fld label="Especialidad"><input value={form.especialidad} onChange={e => setForm(f => ({ ...f, especialidad: e.target.value }))} className="inp" placeholder="Fuerza y Acondicionamiento" /></Fld>
          <Fld label="Biografía"><textarea value={form.bio} onChange={e => setForm(f => ({ ...f, bio: e.target.value }))} rows={3} className="inp resize-none" placeholder="Breve descripción..." /></Fld>
          <Fld label="Foto URL"><input value={form.foto_url} onChange={e => setForm(f => ({ ...f, foto_url: e.target.value }))} className="inp" placeholder="https://..." /></Fld>
          <Fld label="Estado">
            <button type="button" onClick={() => setForm(f => ({ ...f, activo: !f.activo }))} className={`w-full py-2 text-[11px] font-black tracking-widest uppercase border transition-colors ${form.activo ? "bg-green-500/10 border-green-500/40 text-green-400" : "bg-red-500/10 border-red-500/30 text-red-400"}`}>
              {form.activo ? "Activo" : "Inactivo"}
            </button>
          </Fld>
          {err && <p className="text-red-400 text-xs">{err}</p>}
          <ModalActions onSave={handleSave} onCancel={() => setModal(null)} saving={saving} label={modal === "create" ? "Crear" : "Guardar"} />
        </Modal>
      )}
    </div>
  );
}

// ── DISPONIBILIDAD ────────────────────────────────────────────────────────────
function DisponibilidadSection() {
  const [trainers, setTrainers] = useState([]);
  const [selectedTrainer, setSelectedTrainer] = useState("");
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ tipo: "semanal", dia_semana: 0, fecha: "", hora_inicio: "09:00", hora_fin: "20:00", duracion_minutos: 60, max_concurrent: 1, activo: true });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/admin/trainers").then(r => {
      setTrainers(r.data);
      if (r.data.length > 0) setSelectedTrainer(r.data[0].id);
    });
  }, []);

  const loadSchedules = useCallback(async () => {
    if (!selectedTrainer) return;
    setLoading(true);
    try {
      const data = await api.get(`/admin/schedules?trainer_id=${selectedTrainer}`).then(r => r.data);
      setSchedules(data);
    } finally { setLoading(false); }
  }, [selectedTrainer]);

  useEffect(() => { loadSchedules(); }, [loadSchedules]);

  const handleCreate = async () => {
    setSaving(true); setErr("");
    try {
      await api.post("/admin/schedules", { ...form, trainer_id: selectedTrainer, dia_semana: form.tipo === "semanal" ? Number(form.dia_semana) : null });
      setAdding(false); await loadSchedules();
    } catch (e) { setErr(formatApiError(e?.response?.data?.detail) || "Error"); }
    finally { setSaving(false); }
  };

  const handleToggle = async (s) => {
    try {
      await api.put(`/admin/schedules/${s.id}`, { ...s, trainer_id: selectedTrainer, activo: !s.activo });
      await loadSchedules();
    } catch { }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Eliminar este horario?")) return;
    await api.delete(`/admin/schedules/${id}`); await loadSchedules();
  };

  const weekly = schedules.filter(s => s.tipo === "semanal").sort((a, b) => a.dia_semana - b.dia_semana);
  const specific = schedules.filter(s => s.tipo === "fecha_especifica").sort((a, b) => a.fecha?.localeCompare(b.fecha || "") || 0);

  if (trainers.length === 0) return <Empty icon={Users} text="Crea entrenadores primero." />;

  return (
    <div data-testid="disponibilidad-section">
      <div className="flex flex-wrap items-center gap-3 mb-6">
        <select value={selectedTrainer} onChange={e => setSelectedTrainer(e.target.value)}
          className="bg-black border border-white/12 text-white px-4 py-2 text-sm outline-none focus:border-[#FFCC00]">
          {trainers.map(t => <option key={t.id} value={t.id}>{t.nombre}</option>)}
        </select>
        <button onClick={() => { setAdding(true); setErr(""); }} data-testid="add-schedule-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#FFCC00] text-black font-black text-[10px] tracking-[0.2em] uppercase hover:bg-white transition-colors">
          <Plus size={12} /> Añadir horario
        </button>
      </div>

      {/* Weekly template */}
      <div className="mb-6">
        <p className="text-[10px] tracking-[0.25em] uppercase font-black text-white/40 mb-3">Plantilla semanal</p>
        {weekly.length === 0 ? <p className="text-white/25 text-sm">Sin horarios semanales.</p> : (
          <div className="border border-white/10 divide-y divide-white/5">
            {weekly.map(s => (
              <ScheduleRow key={s.id} schedule={s} onToggle={() => handleToggle(s)} onDelete={() => handleDelete(s.id)} />
            ))}
          </div>
        )}
      </div>

      {/* Specific dates */}
      <div>
        <p className="text-[10px] tracking-[0.25em] uppercase font-black text-white/40 mb-3">Fechas específicas</p>
        {specific.length === 0 ? <p className="text-white/25 text-sm">Sin fechas específicas.</p> : (
          <div className="border border-white/10 divide-y divide-white/5">
            {specific.map(s => (
              <ScheduleRow key={s.id} schedule={s} onToggle={() => handleToggle(s)} onDelete={() => handleDelete(s.id)} />
            ))}
          </div>
        )}
      </div>

      {adding && (
        <Modal title="Añadir horario" onClose={() => setAdding(false)}>
          <Fld label="Tipo">
            <div className="flex gap-2">
              {["semanal", "fecha_especifica"].map(t => (
                <button key={t} type="button" onClick={() => setForm(f => ({ ...f, tipo: t }))}
                  className={`flex-1 py-2 text-[10px] font-black tracking-widest uppercase border transition-colors ${form.tipo === t ? "bg-[#FFCC00] border-[#FFCC00] text-black" : "border-white/15 text-white/45 hover:text-white"}`}>
                  {t === "semanal" ? "Semanal" : "Fecha específica"}
                </button>
              ))}
            </div>
          </Fld>
          {form.tipo === "semanal" ? (
            <Fld label="Día de la semana">
              <select value={form.dia_semana} onChange={e => setForm(f => ({ ...f, dia_semana: e.target.value }))} className="inp appearance-none">
                {DAYS.map((d, i) => <option key={i} value={i}>{d}</option>)}
              </select>
            </Fld>
          ) : (
            <Fld label="Fecha"><input type="date" value={form.fecha} onChange={e => setForm(f => ({ ...f, fecha: e.target.value }))} className="inp" /></Fld>
          )}
          <div className="grid grid-cols-2 gap-4">
            <Fld label="Hora inicio">
              <select value={form.hora_inicio} onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))} className="inp appearance-none">
                {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </Fld>
            <Fld label="Hora fin">
              <select value={form.hora_fin} onChange={e => setForm(f => ({ ...f, hora_fin: e.target.value }))} className="inp appearance-none">
                {HOURS.map(h => <option key={h} value={h}>{h}</option>)}
              </select>
            </Fld>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Fld label="Duración (min)">
              <select value={form.duracion_minutos} onChange={e => setForm(f => ({ ...f, duracion_minutos: Number(e.target.value) }))} className="inp appearance-none">
                {[30, 45, 60, 90, 120].map(d => <option key={d} value={d}>{d} min</option>)}
              </select>
            </Fld>
            <Fld label="Máx. simultáneos">
              <input type="number" min="1" max="20" value={form.max_concurrent} onChange={e => setForm(f => ({ ...f, max_concurrent: Number(e.target.value) }))} className="inp" />
            </Fld>
          </div>
          <Fld label="Estado">
            <button type="button" onClick={() => setForm(f => ({ ...f, activo: !f.activo }))} className={`w-full py-2 text-[11px] font-black tracking-widest uppercase border transition-colors ${form.activo ? "bg-green-500/10 border-green-500/40 text-green-400" : "bg-white/5 border-white/15 text-white/40"}`}>
              {form.activo ? "Activo" : "Inactivo (bloqueo)"}
            </button>
          </Fld>
          {err && <p className="text-red-400 text-xs">{err}</p>}
          <ModalActions onSave={handleCreate} onCancel={() => setAdding(false)} saving={saving} label="Guardar horario" />
        </Modal>
      )}
    </div>
  );
}

function ScheduleRow({ schedule: s, onToggle, onDelete }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 gap-3" data-testid={`schedule-row-${s.id}`}>
      <div className="flex items-center gap-3 min-w-0">
        <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 border ${s.activo ? "border-green-500/30 text-green-400" : "border-red-500/20 text-red-400"}`}>
          {s.activo ? "Activo" : "Bloqueado"}
        </span>
        <div>
          <p className="text-sm font-bold text-white">
            {s.tipo === "semanal" ? DAYS[s.dia_semana] : s.fecha} · {s.hora_inicio}–{s.hora_fin}
          </p>
          <p className="text-[10px] text-white/35">{s.duracion_minutos} min · máx. {s.max_concurrent} simultáneo{s.max_concurrent !== 1 ? "s" : ""}</p>
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <button onClick={onToggle} className="px-2.5 py-1.5 border border-white/10 text-white/35 hover:border-[#FFCC00]/40 hover:text-[#FFCC00] text-[9px] font-bold uppercase tracking-wider transition-colors">
          {s.activo ? "Bloquear" : "Activar"}
        </button>
        <button onClick={onDelete} className="p-1.5 border border-white/10 text-white/25 hover:border-red-500/40 hover:text-red-400 transition-colors"><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

// ── RESERVAS (admin list) ─────────────────────────────────────────────────────
function ReservasSection() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/bookings").then(r => { setBookings(r.data); setLoading(false); });
  }, []);

  const STATUS_COLOR = { confirmada: "text-green-400 border-green-500/30", cancelada: "text-red-400 border-red-500/20", completada: "text-white/40 border-white/15" };

  return (
    <div data-testid="reservas-list-section">
      {loading ? <Spinner /> : bookings.length === 0 ? <Empty icon={CalendarDays} text="No hay reservas todavía." /> : (
        <div className="border border-white/10 divide-y divide-white/5">
          <div className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-3 px-4 py-2 bg-white/3">
            {["Socio", "Entrenador / Hora", "Duración", "Estado", "Creada"].map(h => (
              <p key={h} className="text-[9px] font-black tracking-[0.2em] uppercase text-white/30">{h}</p>
            ))}
          </div>
          {bookings.map(b => (
            <div key={b.id} className="grid grid-cols-[1fr_1fr_auto_auto_auto] gap-3 px-4 py-3 items-center hover:bg-white/[0.02]" data-testid={`booking-row-${b.id}`}>
              <div>
                <p className="text-sm font-bold text-white truncate">{b.user_nombre || "—"}</p>
                <p className="text-[10px] text-white/30">{b.user_email}</p>
              </div>
              <div>
                <p className="text-sm text-white/70 font-bold">{b.trainer_nombre}</p>
                <p className="text-[10px] text-white/30">{b.fecha_hora?.replace("T", " ").slice(0, 16)}</p>
              </div>
              <span className="text-xs text-white/50">{b.duracion_minutos} min</span>
              <span className={`text-[9px] font-black tracking-widest uppercase px-2 py-0.5 border ${STATUS_COLOR[b.estado] || "text-white/30 border-white/10"}`}>{b.estado}</span>
              <span className="text-[10px] text-white/25">{b.created_at?.slice(0, 10)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── CONFIG ────────────────────────────────────────────────────────────────────
function ConfigSection() {
  const [cfg, setCfg] = useState({ antelacion_minima_horas: 24, cancelacion_horas: 24 });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/booking-config").then(r => setCfg(r.data)).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true); setSaved(false);
    try {
      await api.put("/admin/booking-config", cfg);
      setSaved(true); setTimeout(() => setSaved(false), 2000);
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-md space-y-5" data-testid="config-section">
      <Fld label="Antelación mínima para reservar (horas)">
        <input type="number" min="0" value={cfg.antelacion_minima_horas}
          onChange={e => setCfg(c => ({ ...c, antelacion_minima_horas: Number(e.target.value) }))}
          className="inp" data-testid="cfg-antelacion" />
        <p className="text-[10px] text-white/25 mt-1">El cliente no puede reservar slots que empiecen en menos de X horas.</p>
      </Fld>
      <Fld label="Horas mínimas para cancelar">
        <input type="number" min="0" value={cfg.cancelacion_horas}
          onChange={e => setCfg(c => ({ ...c, cancelacion_horas: Number(e.target.value) }))}
          className="inp" data-testid="cfg-cancelacion" />
        <p className="text-[10px] text-white/25 mt-1">El cliente puede cancelar solo si quedan más de X horas para la cita.</p>
      </Fld>
      <button onClick={handleSave} disabled={saving} data-testid="save-config-btn"
        className="flex items-center gap-2 px-6 py-2.5 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40">
        {saving ? <Loader2 size={13} className="animate-spin" /> : saved ? <Check size={13} /> : <Save size={13} />}
        {saved ? "¡Guardado!" : "Guardar configuración"}
      </button>
    </div>
  );
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-2xl bg-[#0d0d0d] border border-white/10 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="sticky top-0 bg-[#0d0d0d] border-b border-white/10 px-5 py-4 flex items-center justify-between z-10">
          <p className="text-sm font-black text-white">{title}</p>
          <button onClick={onClose} className="text-white/30 hover:text-white"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">{children}</div>
      </div>
      <style>{`.inp{width:100%;background:#111;border:1px solid rgba(255,255,255,.12);color:#fff;padding:10px 14px;font-size:13px;outline:none;transition:border-color .15s}.inp:focus{border-color:#FFCC00}`}</style>
    </div>
  );
}

function ModalActions({ onSave, onCancel, saving, label }) {
  return (
    <div className="flex gap-2 pt-1">
      <button onClick={onSave} disabled={saving}
        className="flex-1 flex items-center justify-center gap-2 py-3 bg-[#FFCC00] text-black font-black text-[11px] tracking-[0.2em] uppercase hover:bg-white transition-colors disabled:opacity-40">
        {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}{label}
      </button>
      <button onClick={onCancel} className="px-4 border border-white/15 text-white/40 text-[11px] font-bold uppercase tracking-wider hover:text-white transition-colors">Cancelar</button>
    </div>
  );
}
function Fld({ label, children }) {
  return <div><label className="block text-[10px] tracking-[0.2em] uppercase font-bold text-white/45 mb-1.5">{label}</label>{children}</div>;
}
function Spinner() { return <div className="flex justify-center py-16"><Loader2 size={24} className="animate-spin text-[#FFCC00]" /></div>; }
function Empty({ icon: Icon, text }) {
  return <div className="border border-white/8 border-dashed p-14 text-center"><Icon size={32} className="mx-auto text-white/10 mb-3" /><p className="text-white/30 text-sm">{text}</p></div>;
}
